import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate  } from "react-router-dom";
import { useSelector } from "react-redux";

import ParkTable from "components/TransportComponents/ParkTable";
import { TransportService } from "service/TransportService";
import Loading from "components/Loading";
import Pagination from "components/common/Layout/Pagination";
import AddTransport from "components/SitePages/Profile/AddTransport";
import { PROFILE } from "helpers/routes";
import Input from "components/common/Input";
import { KEYS } from "constants/keyCode";
import { ITEMS_PER_PAGE } from "constants/settings";
import { EMPLOYEE_ROLES } from "constants/user";
import { MODAL_ID } from "constants/settings";
import ReactDOM from "react-dom";

import styles from "./styles.module.scss";

const Park = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [currentOffset, setCurrentOffset] = useState(null);
    const [loading, setLoading] = useState(false);
    const [transport, setTransport] = useState({ items: [] });
    const [searchValue, setSearchValue] = useState("");
    const currentLang = useSelector((state) => state.settings.lang);
    const user = useSelector((state) => state.profile.profileData);
    const isOwner = user.employee_company_roles === EMPLOYEE_ROLES.OWNER;
    const [addModalOpen, setAddModalOpen] = useState(false);

    const prevLangRef = useRef();

    const fetch = useCallback(
        (urlOffset) => {
            setLoading(true);
            setCurrentOffset(urlOffset);

            return TransportService.getTransportTrackpark({
                offset: urlOffset,
                lang: currentLang,
                limit: ITEMS_PER_PAGE,
                registration_number: searchValue || null,
                companyId: user.company_id,
            })
                .then((res) => {
                    setTransport(res.data);
                    window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                    });
                })
                .catch((e) => {
                    console.log(e.response);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [currentLang, searchValue, user.company_id],
    );

    const loadData = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const urlOffset = parseInt(params.get("offset")) || 0;
        if (urlOffset !== currentOffset || prevLangRef.current !== currentLang) {
            fetch(urlOffset);
        }
    }, [currentLang, currentOffset, fetch, location.search]);

    useEffect(() => {
        if (user.company_id) {
            loadData();
            prevLangRef.current = currentLang;
        }
    }, [loadData, currentLang, user.company_id]);

    const handleSearch = () => {
        navigate({
            search: "?offset=0",
        });
        fetch(0);
    };

    const handleKeyPress = (event) => {
        event.persist();
        if (event.charCode === KEYS.ENTER) {
            handleSearch();
        }
    };

    const handleChange = (event) => {
        event.persist();
        setSearchValue(event.target.value);
    };

    const hasPagination = transport.count / ITEMS_PER_PAGE > 1;

    const handleOpenAddModal = () => setAddModalOpen(true);
    const handleCloseAddModal = () => setAddModalOpen(false);
    const handleAddSuccess = () => {
        setAddModalOpen(false);
        fetch(currentOffset || 0);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <span className={styles.title}>
                    {t("park.title")} ({transport.count || 0})
                </span>
                <div className={styles.actions}>
                    <Input
                        wrapperClass={styles.input}
                        onChange={handleChange}
                        onKeyPress={handleKeyPress}
                        placeholder={t("park.search_field_grey")}
                        postfix={<button className={styles.buttonSearch} onClick={handleSearch} />}
                    />
                    {isOwner && (
                        <button
                            type="button"
                            className={styles.addButton}
                            onClick={handleOpenAddModal}
                        >
                            + {t("park.add_button")}
                        </button>
                    )}
                </div>
            </div>
            {loading ? <Loading /> : <ParkTable data={transport} update={fetch} onAdd={isOwner ? handleOpenAddModal : undefined} />}
            <div className={styles.paginationContainer}>
                {hasPagination && (
                    <Pagination
                        count={transport.count}
                        url={PROFILE.park}
                        itemsOnPage={ITEMS_PER_PAGE}
                    />
                )}
            </div>
            {addModalOpen && ReactDOM.createPortal(
                <div className="overlay">
                    <div className="overlay__wrapper" id="overlay">
                        <div className="popup">
                            <AddTransport
                                onClose={handleCloseAddModal}
                                onSuccess={handleAddSuccess}
                            />
                        </div>
                    </div>
                </div>,
                document.getElementById(MODAL_ID),
            )}
        </div>
    );
};

export default Park;
